import React from 'react'
import { Box, Grid, Typography } from '@mui/material'
import type { GridProps } from '@mui/material'
import { poolwerxColors } from '../../../../theme/colors'

export interface AdminFormSectionProps {
  /** Teal section heading. */
  title: string
  /** Optional caption rendered under the heading (caption styling). */
  caption?: React.ReactNode
  /** Optional node rendered right-aligned in the heading row (e.g. a status chip). */
  action?: React.ReactNode
  /**
   * Column sizing for the section's `<Grid item>`. Defaults to full width.
   * Pass e.g. `{ md: 4 }` / `{ md: 8 }` for side-by-side sections.
   */
  gridProps?: GridProps
  /**
   * Render the section as a lightweight bordered panel (hairline border, subtle
   * off-white fill, no elevation) instead of the bare heading + spacing. Use to
   * group fields without the heavy "card inside a card" look.
   */
  card?: boolean
  /** Tighter vertical padding for compact panels (e.g. a single-row toggle). */
  dense?: boolean
  children: React.ReactNode
}

/**
 * Standard admin add/edit form section: a teal `h6` heading (+ optional
 * caption) over its fields, rendered as a `<Grid item>` so sections compose
 * inside a `<Grid container>`. Centralises the heading/spacing convention
 * previously hand-rolled in every admin form (see docs/development/admin-forms.md).
 */
const AdminFormSection: React.FC<AdminFormSectionProps> = ({
  title,
  caption,
  action,
  gridProps,
  card = false,
  dense = false,
  children,
}) => (
  <Grid item xs={12} {...gridProps}>
    <Box
      sx={
        card
          ? {
              px: 2.5,
              py: dense ? 1.75 : 2.5,
              borderRadius: '8px',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
            }
          : { px: 3, py: 0, borderRadius: 1, mb: 2 }
      }
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: caption ? 0.5 : '8px',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ color: poolwerxColors.teal, fontWeight: 700, fontSize: '16px' }}
        >
          {title}
        </Typography>
        {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
      </Box>
      {caption && (
        <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {caption}
        </Typography>
      )}
      {children}
    </Box>
  </Grid>
)

export default AdminFormSection
